import { useState, type ReactNode } from 'react';
import { UnitfieldIcon } from '@unitfield/ui/icons';
import { cn } from '@unitfield/ui/lib/utils';
import { PublicFooter, PublicTopBar } from '../../shell';
import { initials as initialsOf } from '../../shell/public-branding';
import type { CardLine } from './application-card-model';
import styles from './apply.module.css';

export type ApplyScreenKind = 'start' | 'code' | 'step' | 'sign';

/**
 * Two equal columns (spec §5): the question on the left, the home's photos on the right.
 * On a phone the photo leads, the card rides over it and the question follows.
 */
export function ApplyFrame({
  kind,
  topTrailing,
  panel,
  actions,
  children,
}: {
  kind: ApplyScreenKind;
  topTrailing: ReactNode;
  panel: ReactNode;
  /**
   * The step's Back / Continue row. It lives inside the form column, after the form and before the footer, and sticks to the foot of the column's own
   * scroll area: it rests under the last field, never over the footer, and never floats over the page.
   */
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={styles.frame} data-kind={kind}>
      <div className={styles.side}>
        <div className={styles.topBar}><PublicTopBar trailing={topTrailing} /></div>
        <main className={styles.main}>
          <div className={styles.step}>{children}</div>
        </main>
        {actions ? <div className={styles.actions} data-apply-actions="">{actions}</div> : null}
        <div className={styles.footer}><PublicFooter /></div>
      </div>
      <div className={styles.panel}>{panel}</div>
    </div>
  );
}

/** The home's photos, one per step, with an "N / M" chip. Rooms are never named; the data has none. */
export function PhotoPanel({ photos, index, children }: { photos: string[]; index: number; children?: ReactNode }) {
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());
  const usable = photos.filter((photo) => !failed.has(photo));
  const position = usable.length > 0 ? ((index % usable.length) + usable.length) % usable.length : 0;
  const photo = usable[position];
  return (
    <section className={styles.photoPanel} aria-label="Photos of this home">
      <div className={styles.photo} data-empty={photo ? undefined : ''}>
        {photo ? (
          <img
            key={photo}
            src={photo}
            alt=""
            draggable={false}
            onError={() => setFailed((current) => new Set(current).add(photo))}
          />
        ) : (
          <span className={styles.photoTile} aria-hidden="true"><UnitfieldIcon name="home" size={26} /></span>
        )}
        {usable.length > 1 ? (
          <span className={cn(styles.count, 'num')}>
            <UnitfieldIcon name="image" size={14} aria-hidden="true" />
            {position + 1} / {usable.length}
          </span>
        ) : null}
        {children}
      </div>
    </section>
  );
}

/** Before the code: what the applicant is applying for, and whether it is ready on their date. */
export function HomeCard({
  title,
  address,
  rent,
  moveIn,
  fit,
}: {
  title: string;
  address: string | null;
  rent: string | null;
  moveIn: string | null;
  fit: { tone: 'ok' | 'later' | 'none'; text: string } | null;
}) {
  return (
    <div className={styles.overCard} data-variant="home">
      <p className={styles.homeTitle}>{title}</p>
      {address ? <p className={styles.homeAddress}>{address}</p> : null}
      <div className={styles.homeFacts}>
        {rent ? <span className={cn(styles.homeRent, 'num')}>{rent}<small> / month</small></span> : null}
        {moveIn ? <span className={cn(styles.homeMoveIn, 'num')}><UnitfieldIcon name="calendar" size={16} aria-hidden="true" />Moving in {moveIn}</span> : null}
      </div>
      {fit ? (
        <p className={styles.fit} data-tone={fit.tone}>
          <span className={styles.fitDot} aria-hidden="true" />
          {fit.text}
        </p>
      ) : null}
    </div>
  );
}

export interface ApplicationCardProps {
  businessName: string;
  applicantName: string;
  unitLabel: string;
  step: number;
  steps: number;
  ready: boolean;
  lines: CardLine[];
  /** Sign and send: every line gets a visible "Edit". */
  review: boolean;
  /** Jumps to a section; only for lines the applicant has already reached. */
  onEdit?: (line: CardLine) => void;
  canEdit?: (line: CardLine) => boolean;
}

/**
 * "What {business} will see": the application itself, filling in line by line as the applicant
 * types. The current line wears the brand ring and updates live; lines truncate, never wrap.
 */
export function ApplicationCard({ businessName, applicantName, unitLabel, step, steps, ready, lines, review, onEdit, canEdit }: ApplicationCardProps) {
  const percent = ready ? 100 : Math.round((Math.min(step, steps) / Math.max(steps, 1)) * 100);
  return (
    <div className={styles.overCard} data-variant="application" data-review={review ? '' : undefined}>
      <p className={styles.cardLabel}>What {businessName || 'the rental business'} will see</p>
      <div className={styles.who}>
        <span className={styles.avatar} aria-hidden="true">{initialsOf(applicantName) || '·'}</span>
        <div className={styles.whoText}>
          <p className={styles.whoName}>{applicantName || 'You'}</p>
          <p className={styles.whoFor}>Applying for {unitLabel}</p>
        </div>
        <span className={cn(styles.mini, 'num')}>
          <i aria-hidden="true"><b style={{ width: `${percent}%` }} /></i>
          {ready ? 'Ready' : <span><span className={styles.miniLong}>{step} of {steps}</span><span className={styles.miniShort}>{step}/{steps}</span></span>}
        </span>
      </div>
      <ul className={styles.lines} aria-label="Your application">
        {lines.map((line) => {
          const editable = Boolean(onEdit) && line.state !== 'current' && (canEdit?.(line) ?? true);
          const content = (
            <>
              <span className={styles.lineMark} aria-hidden="true">
                {line.state === 'done' ? <UnitfieldIcon name="check" size={12} strokeWidth={2.8} /> : null}
              </span>
              <span className={styles.lineKey}>{line.label}</span>
              <span className={cn(styles.lineValue, 'num')}>{line.value}</span>
            </>
          );
          return (
            <li key={line.key} className={styles.line} data-state={line.state}>
              {editable && !review ? (
                <button type="button" className={styles.lineButton} onClick={() => onEdit?.(line)} aria-label={`Edit ${line.label}`}>{content}</button>
              ) : (
                <div className={styles.lineButton}>{content}</div>
              )}
              {review && editable ? (
                <button type="button" className={styles.lineEdit} onClick={() => onEdit?.(line)} aria-label={`Edit ${line.label}`}>Edit</button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
